import { Formik } from 'formik'
import React, { useContext } from 'react'
import { customerNotificationOptions } from '../../../common/options'

import { Button } from '../../../components/Button'
import {
  ButtonColumn,
  ButtonRow,
  FormSubGroup,
  FormSubGroupHeader,
  FormSubGroupHeading,
  FormSubGroupWrapper,
  FormSubGroupsList,
  Section,
  SectionColumn,
} from '../../../components/layout'
import {
  EmailInput,
  PersonalIdentityCodeInput,
  PhoneInput,
  SelectInput,
  TextAreaInput,
  TextInput,
} from '../../../components/layout/form/input'
import { ScrollToError } from '../../../components/layout/form/ScrollToError'
import { FormikForm as Form } from '../../../components/layout/form/style'
import { formatMessageSafe, translateInputOptions } from '../../../util/translateInputOptions'
import { StateStore } from '../../StoreProvider'
import { acceptedRoles } from '../../TowingOrderPage/AddTowingOrder/TowingOrderSettingsForm'
import { deliveryMethodPersonOptions } from '../options'
import { EditPersonResponse, PersonCustomerFormValues } from '../types'
import { EditPersonCustomerResponse } from './types'
import { useIntl } from 'react-intl'
import { useSettings } from '../../Parameters'
import { messages } from '../messages'

interface FormProps {
  editPersonCustomer: (values: PersonCustomerFormValues) => any
  onCloseForm: (t: EditPersonResponse) => void
  onCancelForm: () => void
  initialValues: PersonCustomerFormValues
  loading: boolean
}

const EditPersonCustomerFormIntl: React.FunctionComponent<FormProps> = ({
  onCancelForm,
  onCloseForm,
  editPersonCustomer,
  initialValues,
  loading,
}) => {
  const intl = useIntl();
  const formatMessage = formatMessageSafe(intl)
  const translatedDeliveryMethodOptions = translateInputOptions(deliveryMethodPersonOptions, formatMessage)
  const settings = useSettings()
  const {
    state: { currentUser },
  } = useContext(StateStore)

  const disableSsn = !!initialValues.ssn

  const handleSubmit = async (values: PersonCustomerFormValues) => {
    try {
      const fullResponse = await editPersonCustomer(values)
      const response: EditPersonCustomerResponse = fullResponse.data
      if (response.editPersonCustomer.__typename === 'EditPersonCustomerSuccess') {
        onCloseForm(response.editPersonCustomer)
      } else {
        console.log('Error saving: ', response.editPersonCustomer.__typename)
      }
    } catch (err) {
      console.log('Error updating customer', err)
    }
  }
  return (
    <Formik
      initialValues={initialValues}
      onSubmit={(values: PersonCustomerFormValues) => {
        handleSubmit(values)
      }}
    >
      {({ values, submitForm }) => {
        return (
          <Form>
            <FormSubGroupsList>
              <FormSubGroupWrapper>
                <FormSubGroupHeader>
                  <FormSubGroupHeading>
                    {formatMessage(messages.customerRegistryHeading, {
                      name: `${values.firstName} ${values.lastName}`,
                    })}
                  </FormSubGroupHeading>
                </FormSubGroupHeader>

                <FormSubGroup>
                  <Section>
                    <SectionColumn>
                      <TextInput label={formatMessage(messages.identifier)} name="identifier" />
                    </SectionColumn>
                    <SectionColumn>
                      <PersonalIdentityCodeInput label={formatMessage(messages.ssn)} name="ssn" disabled={disableSsn} />
                    </SectionColumn>
                    <SectionColumn>
                      <TextInput label={formatMessage(messages.firstName)} name="firstName" />
                    </SectionColumn>
                    <SectionColumn>
                      <TextInput label={formatMessage(messages.lastName)} name="lastName" />
                    </SectionColumn>
                    <SectionColumn>
                      <TextInput label="c/o" name="co" />
                    </SectionColumn>
                    <SectionColumn>
                      <TextInput label={formatMessage(messages.address)} name="address"></TextInput>
                    </SectionColumn>
                    <SectionColumn>
                      <TextInput label={formatMessage(messages.zipcode)} name="zipcode"></TextInput>
                    </SectionColumn>
                    <SectionColumn>
                      <TextInput label={formatMessage(messages.city)} name="city"></TextInput>
                    </SectionColumn>
                    <SectionColumn>
                      <EmailInput label={formatMessage(messages.emailAddress)} name="email" />
                    </SectionColumn>
                    <SectionColumn>
                      <PhoneInput label={formatMessage(messages.phone)} name="phone" />
                    </SectionColumn>
                    <SectionColumn>
                      <SelectInput
                        label={formatMessage(messages.deliveryMethodLabel)}
                        name="deliveryMethod"
                        options={translatedDeliveryMethodOptions}
                      />
                    </SectionColumn>
                    <SectionColumn>
                      <TextInput label={formatMessage(messages.extraInfo)} name="extraInfo" />
                    </SectionColumn>
                    {settings &&
                      settings.notifications.enabled &&
                      currentUser &&
                      acceptedRoles.includes(currentUser.role) && (
                        <SectionColumn>
                          <SelectInput
                            label={formatMessage(messages.notificationMethod)}
                            name="notificationMethod"
                            options={translateInputOptions(customerNotificationOptions, formatMessage)}
                            searchable={false}
                          />
                        </SectionColumn>
                      )}
                  </Section>
                  <Section>
                    <SectionColumn>
                      <TextAreaInput label={formatMessage(messages.notes)} name="additionalInfo" />
                    </SectionColumn>
                  </Section>
                  <ButtonRow>
                    <ButtonColumn>
                      <Button category="save" label={formatMessage(messages.save)} onClick={() => submitForm()} loading={loading} />
                    </ButtonColumn>
                    <ButtonColumn>
                      <Button category="cancel" label={formatMessage(messages.close)} onClick={onCancelForm} />
                    </ButtonColumn>
                  </ButtonRow>
                </FormSubGroup>
              </FormSubGroupWrapper>
            </FormSubGroupsList>
            <ScrollToError />
          </Form>
        )
      }}
    </Formik>
  )
}

export const EditPersonCustomerForm = EditPersonCustomerFormIntl
